// "Kitchen recipe card" — the part of Menu → Costing & Recipes where a dish's
// method and its yield with a unit are written (kitchen flow, migration 148).
//
// It has its own draft and its own Save on purpose. The sheet around it
// decides what a dish costs and what it deducts from stock; nothing here can
// change either, and a mistake in one can never block saving the other.
// Renders nothing unless the tenant has FEATURE_KITCHEN_FLOW, so the sheet is
// unchanged everywhere else.
import { useEffect, useMemo, useState, type MutableRefObject } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { RECIPE_CARD_QK } from '@/hooks/useRecipeCards'
import { allUnitOptions } from '@/lib/inventory-units'
import {
  YIELD_UNITS,
  draftFromMethod,
  methodProblems,
  perServing,
  sameMethodDraft,
  toMethodPayload,
  type MethodDraft,
} from '@/lib/recipeCard/methodDraft'
import { RC } from '@/lib/recipeCard/recipeCardStrings'
import { formatQty } from '@/lib/recipeCard/recipeScale'
import { plainError } from '@/components/store/inventory/prep/prepRecipeDraft'
import { RecipeStepsEditor } from './RecipeStepsEditor'

export const RECIPE_METHOD_QK = 'recipe-method'

/**
 * What the sheet around the section needs to know before it lets the user
 * leave: is there unsaved work here, can it be saved, and how.
 */
export interface RecipeMethodGuard {
  dirty: boolean
  canSave: boolean
  /** Resolves true when the method was saved. */
  save: () => Promise<boolean>
}

interface SectionProps {
  productId: string
  showToast?: (t: 'success' | 'error', m: string) => void
  /** Kept up to date while the section is mounted; null when it is not. */
  guardRef?: MutableRefObject<RecipeMethodGuard | null>
}

export function RecipeMethodSection({ productId, showToast, guardRef }: SectionProps) {
  const enabled = useKitchenFlowEnabled()
  if (!enabled) return null
  return <RecipeMethodBody key={productId} productId={productId} showToast={showToast} guardRef={guardRef} />
}

function unitName(u: string) {
  return allUnitOptions.find((o) => o.value === u)?.label ?? u
}

function RecipeMethodBody({ productId, showToast, guardRef }: SectionProps) {
  const qc = useQueryClient()
  const { data: method, isLoading, isError, refetch } = useQuery({
    queryKey: [RECIPE_METHOD_QK, productId],
    queryFn: () => apiClient.getRecipeMethod(productId).then((r) => r.data),
  })
  const saved = useMemo(() => draftFromMethod(method), [method])
  const [draft, setDraft] = useState<MethodDraft>(saved)
  const [serverError, setServerError] = useState<string | null>(null)
  // Adopt the saved method whenever it (re)loads.
  useEffect(() => {
    setDraft(saved)
    setServerError(null)
  }, [saved])

  const save = useMutation({
    mutationFn: () => apiClient.putRecipeMethod(productId, toMethodPayload(draft)),
    onSuccess: (res) => {
      qc.setQueryData([RECIPE_METHOD_QK, productId], res.data)
      // The kitchen's cards and the list of what has one are now out of date.
      qc.invalidateQueries({ queryKey: [RECIPE_CARD_QK] })
      setServerError(null)
      showToast?.('success', RC.saved)
    },
    onError: (e) => setServerError(plainError(e)),
  })

  const dirty = !isLoading && !sameMethodDraft(draft, saved)
  const problems = methodProblems(draft)
  const mutateAsync = save.mutateAsync
  useEffect(() => {
    if (!guardRef) return
    guardRef.current = {
      dirty,
      canSave: problems.length === 0,
      save: () => mutateAsync().then(() => true, () => false),
    }
    return () => {
      guardRef.current = null
    }
  })

  if (isLoading) return null

  const servings = method?.servings ?? 1
  const each = perServing(draft, servings)

  return (
    <section aria-labelledby="recipe-method-title" className="space-y-4 rounded-lg border p-3">
      <div>
        <h4 id="recipe-method-title" className="flex items-center gap-2 text-sm font-semibold">
          <BookOpen className="h-4 w-4" aria-hidden /> {RC.editorTitle}
        </h4>
        <p className="mt-0.5 text-xs text-muted-foreground">{RC.editorHelp}</p>
      </div>

      {isError ? (
        <div className="space-y-2">
          <p className="text-sm text-destructive">{RC.loadFailed}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>{RC.tryAgain}</Button>
        </div>
      ) : (
        <>
          <div>
            <span className="text-sm font-medium">{RC.makesLabel}</span>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Input
                type="number" inputMode="decimal" min={0} step="any" placeholder="0"
                className="h-9 w-24 tabular-nums"
                value={draft.yieldQty}
                onChange={(e) => setDraft((d) => ({ ...d, yieldQty: e.target.value }))}
                aria-label={RC.makesQty}
              />
              <select
                className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={draft.yieldUnit}
                onChange={(e) => setDraft((d) => ({ ...d, yieldUnit: e.target.value }))}
                aria-label={RC.makesUnit}
              >
                <option value="">{RC.noUnit}</option>
                {YIELD_UNITS.map((u) => <option key={u} value={u}>{unitName(u)}</option>)}
              </select>
              {each && <span className="text-xs text-muted-foreground">{RC.makesPortion(formatQty(each.qty, each.unit))}</span>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{RC.makesHelp(servings)}</p>
          </div>

          <RecipeStepsEditor idPrefix={`dish-${productId}`} steps={draft.steps} onChange={(steps) => setDraft((d) => ({ ...d, steps }))} />

          {dirty && problems.map((p) => <p key={p} className="text-sm text-destructive">{p}</p>)}
          {serverError && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>}

          {dirty && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled={problems.length > 0 || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{RC.saving}</> : RC.save}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setDraft(saved); setServerError(null) }}>
                {RC.undo}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
